/**
 * Open a link outside the web view.
 * Native http(s) uses the in-app browser. mailto and tel hand off to Mail
 * and Phone, and the address or number stays visible as the link text when
 * those apps are missing.
 */

import { Browser } from '@capacitor/browser';
import { isNativeApp } from './platform';

export function externalHrefKind(raw) {
  const href = String(raw || '').trim();
  if (!href || href.startsWith('#') || href.startsWith('/') || href.startsWith('?')) return '';
  const lower = href.toLowerCase();
  if (lower.startsWith('mailto:')) return 'mailto';
  if (lower.startsWith('tel:')) return 'tel';
  if (lower.startsWith('http://') || lower.startsWith('https://')) return 'web';
  return '';
}

export function openExternalUrl(url) {
  const target = String(url || '').trim();
  const kind = externalHrefKind(target);
  if (!kind) return Promise.resolve(false);

  if (kind === 'web' && isNativeApp()) {
    const open = Browser && typeof Browser.open === 'function'
      ? Browser.open({ url: target })
      : Promise.reject(new Error('Browser unavailable'));
    return Promise.resolve(open).then(() => true).catch(() => {
      if (typeof window !== 'undefined') window.open(target, '_blank', 'noopener,noreferrer');
      return false;
    });
  }

  if (typeof window === 'undefined') return Promise.resolve(false);
  if (kind === 'mailto' || kind === 'tel' || isNativeApp()) {
    window.location.href = target;
    return Promise.resolve(true);
  }
  window.open(target, '_blank', 'noopener,noreferrer');
  return Promise.resolve(true);
}

/**
 * Bubble-phase guard. React handlers run first, so a hire-gate that
 * preventDefault still wins. In-app paths stay in the web view.
 */
export function installNativeLinkGuard() {
  if (typeof document === 'undefined' || !isNativeApp()) return;
  document.addEventListener('click', (event) => {
    if (event.defaultPrevented) return;
    const node = event.target;
    const link = node && node.closest ? node.closest('a[href]') : null;
    if (!link) return;
    const raw = link.getAttribute('href') || '';
    if (!externalHrefKind(raw)) return;
    event.preventDefault();
    openExternalUrl(link.href || raw);
  });
}
